import { ContactColumns, InvalidImportedContact } from '../types';

const INVALID_CONTACTS_FILE_NAME = 'invalid contacts.csv';

// RFC 4180: a field holding a quote, a comma, a line break or leading/trailing
// whitespace is wrapped in quotes, with inner quotes doubled.
export const csvEscape = (val: unknown): string => {
  const s = val == null ? '' : String(val);
  if (/[",\r\n]/.test(s) || s !== s.trim()) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
};

// Standard contact fields in the order they are written, each named after the column of the
// uploaded file it was read from. The default names are used only for an import that predates
// the recording of its column mapping; those are the names the import itself defaults to.
type BaseField = 'phoneNumber' | 'firstName' | 'lastName' | 'email' | 'birthdate' | 'timeZone';
const BASE_COLUMNS: Array<{ field: BaseField; defaultHeader: string }> = [
  { field: 'phoneNumber', defaultHeader: 'Phone number' },
  { field: 'firstName', defaultHeader: 'First name' },
  { field: 'lastName', defaultHeader: 'Last name' },
  { field: 'email', defaultHeader: 'Email' },
  { field: 'birthdate', defaultHeader: 'Birthdate' },
  { field: 'timeZone', defaultHeader: 'Time zone' },
];

const buildDownloadFileName = (sourceFileName?: string | null): string => {
  const trimmed = sourceFileName?.trim();
  if (!trimmed) return INVALID_CONTACTS_FILE_NAME;

  const baseName = trimmed.replace(/\.[^.]+$/, '');
  return `${baseName} - ${INVALID_CONTACTS_FILE_NAME}`;
};

export const downloadInvalidContactsCsv = (
  items: InvalidImportedContact[],
  sourceFileName?: string | null,
  selectedColumns?: ContactColumns | null,
) => {
  if (!items?.length) return;

  // Name every column the way the uploaded file did, so the download can be fixed and re-uploaded,
  // and leave out the fields that file had no column for
  const baseColumns = BASE_COLUMNS
    .map(({ field, defaultHeader }) => ({
      field,
      header: selectedColumns ? selectedColumns[field]?.trim() : defaultHeader,
    }))
    .filter((column): column is { field: BaseField; header: string } => !!column.header);
  const metadataKeys = Array.from(new Set(items.flatMap(i => (i.metadata || []).map(m => m.key))));
  const headers = [...baseColumns.map(c => c.header), ...metadataKeys, 'Error'];

  const rows = items.map(i => {
    const metaMap = new Map((i.metadata || []).map(m => [m.key, m.value]));
    const values = [
      ...baseColumns.map(c => i[c.field] || ''),
      ...metadataKeys.map(k => metaMap.get(k) || ''),
      i.error || '',
    ];
    return values.map(csvEscape).join(',');
  });

  const content = [headers.map(csvEscape).join(','), ...rows].join('\r\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildDownloadFileName(sourceFileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
